import type { CampaignRanking, RankingReport } from '@/queries/cpmRanking';
import type { RankingPlatform } from './rankingCompetition';
import { rankingReportStatus } from './rankingPresentation';
import { rankingTrackedViews } from './summarizeRankingViews';

/** How long a clip has waited, in days since it was sent: [0, 2), [2, 4), [4, 7), 7 or more. */
export const REVIEW_QUEUE_AGE_BUCKETS = [
  { key: 'upTo2', belowDays: 2 },
  { key: 'upTo4', belowDays: 4 },
  { key: 'upTo7', belowDays: 7 },
  { key: 'older', belowDays: Infinity },
] as const;
export type ReviewQueueAgeKey = typeof REVIEW_QUEUE_AGE_BUCKETS[number]['key'];

/** Known views as an exact decimal string; clips with no counter are counted apart, never as zero. */
export type ReviewQueueTally = { views: string; clips: number; uncounted: number };

export type RankingReviewQueue = {
  counted: { views: string; clips: number };
  waiting: ReviewQueueTally;
  rejected: { views: string; clips: number };
  platforms: ({ platform: RankingPlatform } & ReviewQueueTally)[];
  ages: ({ key: ReviewQueueAgeKey } & ReviewQueueTally)[];
  oldestCreatedAt: string | null;
  impact: {
    qualifiedNow: number;
    /** Participants not qualified today whose counted views plus known waiting views reach the minimum. */
    wouldQualify: number;
    prizes: number;
    participantsWaiting: number;
    withoutApprovedClip: number;
    /** Share of the known waiting views held by the five largest participants; null with five or fewer. */
    topFiveShare: number | null;
  };
};

type Sum = { views: bigint; clips: number; uncounted: number };
const emptySum = (): Sum => ({ views: 0n, clips: 0, uncounted: 0 });
const add = (sum: Sum, report: RankingReport) => {
  const views = rankingTrackedViews(report);
  sum.clips += 1;
  if (views === null) sum.uncounted += 1;
  else sum.views += BigInt(views);
};
const tally = (sum: Sum): ReviewQueueTally => ({ views: sum.views.toString(), clips: sum.clips, uncounted: sum.uncounted });
const exact = (value: string | null | undefined) => value && /^\d+$/.test(value) ? BigInt(value) : 0n;

/**
 * The reviewer's view of the clips nobody has approved or rejected yet. Only canonical clips
 * (with a submission) count; their tracked views never enter the standings until approved.
 */
export function buildRankingReviewQueue(ranking: CampaignRanking, now: number): RankingReviewQueue {
  const clips = ranking.admin_reports.filter(report => report.submission_id);
  const waiting = emptySum();
  const rejected = emptySum();
  const platforms = new Map<RankingPlatform, Sum>();
  const ages = REVIEW_QUEUE_AGE_BUCKETS.map(() => emptySum());
  const byParticipant = new Map<string, bigint>();
  let oldest: { at: number; value: string } | null = null;

  for (const report of clips) {
    const status = rankingReportStatus(report);
    if (status === 'rejected') add(rejected, report);
    if (status !== 'pending') continue;
    add(waiting, report);
    if (!platforms.has(report.platform)) platforms.set(report.platform, emptySum());
    add(platforms.get(report.platform)!, report);
    byParticipant.set(report.participant_id, (byParticipant.get(report.participant_id) ?? 0n) + exact(rankingTrackedViews(report)));
    const sentAt = Date.parse(report.created_at);
    if (!Number.isFinite(sentAt)) continue;
    const days = (now - sentAt) / 86_400_000;
    add(ages[REVIEW_QUEUE_AGE_BUCKETS.findIndex(bucket => days < bucket.belowDays)], report);
    if (!oldest || sentAt < oldest.at) oldest = { at: sentAt, value: report.created_at };
  }

  const rows = new Map(ranking.rows.map(row => [row.participant_id, row]));
  const minimum = BigInt(Math.max(0, Math.trunc(ranking.config.min_total_views || 0)));
  let wouldQualify = 0;
  let withoutApprovedClip = 0;
  for (const [participant, views] of byParticipant) {
    const row = rows.get(participant);
    if (!row || !row.valid_post_count) withoutApprovedClip += 1;
    if (!row?.eligible && exact(row?.total_views) + views >= minimum) wouldQualify += 1;
  }
  const shares = [...byParticipant.values()].sort((a, b) => (b > a ? 1 : b < a ? -1 : 0));
  const topFive = shares.slice(0, 5).reduce((sum, views) => sum + views, 0n);

  return {
    counted: {
      views: ranking.rows.reduce((sum, row) => sum + exact(row.total_views), 0n).toString(),
      clips: ranking.rows.reduce((sum, row) => sum + row.valid_post_count, 0),
    },
    waiting: tally(waiting),
    rejected: { views: rejected.views.toString(), clips: rejected.clips },
    platforms: [...platforms]
      .map(([platform, sum]) => ({ platform, ...tally(sum) }))
      .sort((a, b) => (BigInt(b.views) > BigInt(a.views) ? 1 : BigInt(b.views) < BigInt(a.views) ? -1 : b.clips - a.clips)),
    ages: REVIEW_QUEUE_AGE_BUCKETS.map((bucket, index) => ({ key: bucket.key, ...tally(ages[index]) })),
    oldestCreatedAt: oldest?.value ?? null,
    impact: {
      qualifiedNow: ranking.rows.filter(row => row.eligible).length,
      wouldQualify,
      prizes: ranking.config.prizes.length,
      participantsWaiting: byParticipant.size,
      withoutApprovedClip,
      topFiveShare: byParticipant.size > 5 && waiting.views > 0n ? Number((topFive * 10_000n) / waiting.views) / 10_000 : null,
    },
  };
}
